export class WCProductConfigurator {
    constructor(configuratorData) {
        this.configuratorData = configuratorData || {};
        this.selected = {};
        this.urlParamPrefix = "conf_";
        this.placeholderUrl =
            document
                .getElementById("product-configurator-root")
                ?.dataset.placeholderUrl || "";
        this.stockValueEl = document.querySelector(
            ".wc-product-stock-status__value[data-configurator-live-stock]"
        );
        if (!this.stockValueEl) {
            this.stockValueEl = document.querySelector(
                ".wc-product-stock-status__value"
            );
        }
        this.currentStockState = {
            unlimited: true,
            qty: null,
        };
        this.init();
        this.headerCartQtyElement = document.querySelector(".header-middle__cart-count.-cart");
    }

    init() {
        this.bindTriggers();
        this.bindSidebarEvents();
        this.bindPricePreviews();
        this.preselectFromUrl();
        this.applyConditions();
        this.applyOptionConditions();
        this.updateRequiredIndicators();
        this.updateSummary();
        this.syncGalleryToSelected();
        this.bindAjaxAddToCart();
        this.bindQuantityChange();
        this.refreshLiveStock();
    }

    findOption(type, value, { by = "key" } = {}) {
        const group = this.configuratorData[type];
        if (!group || value == null) return null;

        const comparator =
            by === "id"
                ? (option) =>
                      option?.id != null &&
                      String(option.id) === String(value)
                : (option) => option?.key === value;

        if (Array.isArray(group.options)) {
            return group.options.find(comparator) || null;
        }

        if (group.groups) {
            const collections = Array.isArray(group.groups)
                ? group.groups
                : Object.values(group.groups);
            for (const subgroup of collections) {
                const options = subgroup?.options || [];
                const found = options.find(comparator);
                if (found) return found;
            }
        }

        return null;
    }

    setSelection(type, option, { silent = false } = {}) {
        if (!option) return;
        this.selected[type] = {
            id: option.id,
            key: option.key,
            label: option.label || option.key,
            price: parseFloat(option.price) || 0,
            sale_price:
                option.sale_price != null ? parseFloat(option.sale_price) : null,
            image_url: option.image_url || null,
            gallery_index: this.normalizeGalleryIndex(option.gallery_index),
        };

        if (!silent) {
            this.syncUrlParams();
        }
    }

    clearSelection(type, { silent = false } = {}) {
        if (this.selected[type]) {
            delete this.selected[type];
            if (!silent) {
                this.syncUrlParams();
            }
        }
    }

    markOptionInput(type, key) {
        const inputs = document.querySelectorAll(`input[name="${type}"]`);
        inputs.forEach((input) => {
            input.checked = input.value === key;
        });
    }

    applySelection(
        type,
        value,
        { markInput = false, silent = false, by = "key" } = {}
    ) {
        const option = this.findOption(type, value, { by });
        if (!option) return null;
        this.setSelection(type, option, { silent });
        if (markInput) this.markOptionInput(type, option.key);
        return option;
    }

    preselectFromUrl() {
        const search = window.location?.search || "";
        if (!search) return;

        const params = new URLSearchParams(search);
        let mutated = false;

        for (const [paramKey, value] of params.entries()) {
            if (!paramKey.startsWith(this.urlParamPrefix)) continue;
            const type = paramKey.slice(this.urlParamPrefix.length);
            if (!type) continue;
            const option =
                this.applySelection(type, value, {
                    markInput: true,
                    silent: true,
                    by: "id",
                }) ||
                this.applySelection(type, value, {
                    markInput: true,
                    silent: true,
                    by: "key",
                });
            if (!option) {
                params.delete(paramKey);
                mutated = true;
            } else if (
                option.id != null &&
                String(value) !== String(option.id)
            ) {
                params.set(paramKey, option.id);
                mutated = true;
            }
        }

        if (mutated && window.history?.replaceState) {
            const nextQuery = params.toString();
            const nextUrl = `${window.location.pathname}${
                nextQuery ? `?${nextQuery}` : ""
            }${window.location.hash}`;
            window.history.replaceState({}, "", nextUrl);
        }
    }

    syncUrlParams() {
        if (!window.history?.replaceState) return;
        const params = new URLSearchParams(window.location.search);
        const activeKeys = new Set();

        Object.entries(this.selected).forEach(([type, data]) => {
            const paramKey = `${this.urlParamPrefix}${type}`;
            const paramValue =
                data?.id != null ? data.id : data?.key;
            if (paramValue == null) return;
            params.set(paramKey, paramValue);
            activeKeys.add(paramKey);
        });

        const toDelete = [];
        for (const key of params.keys()) {
            if (
                key.startsWith(this.urlParamPrefix) &&
                !activeKeys.has(key)
            ) {
                toDelete.push(key);
            }
        }
        toDelete.forEach((key) => params.delete(key));

        const currentQuery = window.location.search.replace(/^\?/, "");
        const nextQuery = params.toString();
        if (currentQuery === nextQuery) return;

        const nextUrl = `${window.location.pathname}${
            nextQuery ? `?${nextQuery}` : ""
        }${window.location.hash}`;
        window.history.replaceState({}, "", nextUrl);
    }

    bindTriggers() {
        document
            .querySelectorAll("[data-configurator-open]")
            .forEach((el) => {
                el.addEventListener("click", () => {
                    const type = el.dataset.configuratorOpen;
                    this.openSidebar(type);
                });
            });

        document.addEventListener("mda_variant_changed", () => {
            this.refreshLiveStock();
            this.updateAddToCartButton();
        });
    }

    bindSidebarEvents() {
        document.body.addEventListener("click", (e) => {
            if (e.target.classList.contains("js-sidebar-close")) {
                return this.closeSidebar();
            }

            if (e.target.classList.contains("js-sidebar-confirm")) {
                const type = e.target.dataset.type;
                const sidebar = document.querySelector(
                    `.js-configurator-sidebar[data-type="${type}"]`
                );
                if (!sidebar) return;

                const sel = sidebar.querySelector(
                    `input[name="${type}"]:checked`
                );
                const option =
                    sel && sel.value && this.applySelection(type, sel.value);
                if (!option) {
                    if (this.getMissingRequiredGroups().includes(type)) {
                        alert("Wybierz wymaganą opcję.");
                        return;
                    }
                    this.clearSelection(type);
                }
                if (option) {
                    this.updateGallerySelection(option);
                }

                this.closeSidebar();
                this.applyConditions();
                this.applyOptionConditions();
                this.updateRequiredIndicators();
                this.updateSummary();
                this.refreshLiveStock();
            }
        });
    }

    applyConditions() {
        Object.entries(this.configuratorData).forEach(([k, g]) => {
            const li = document.querySelector(
                `.wc-product-configurator__item[data-configurator-open="${k}"]`
            );
            if (!li) return;

            const conditions = this.normalizeShowIfConditions(g?.show_if || g?.condition);
            const hasVisibleOptions = this.groupHasVisibleOptions(k);
            if (!conditions.length && hasVisibleOptions) {
                li.style.display = "";
                return;
            }

            const ok = conditions.length
                ? conditions.some((c) => this.evaluateShowIfCondition(c))
                : hasVisibleOptions;

            li.style.display = ok ? "" : "none";
            if (!ok) this.clearSelection(k);
        });
    }

    isGroupVisible(type) {
        const g = this.configuratorData[type];
        if (!g) return false;
        const conditions = this.normalizeShowIfConditions(g?.show_if || g?.condition);
        const hasVisibleOptions = this.groupHasVisibleOptions(type);
        if (!conditions.length) return hasVisibleOptions;
        return conditions.some((c) => this.evaluateShowIfCondition(c)) && hasVisibleOptions;
    }

    isGroupRequired(type) {
        const g = this.configuratorData[type];
        return !!g?.required && this.isGroupVisible(type);
    }

    getMissingRequiredGroups() {
        const missing = [];
        Object.keys(this.configuratorData).forEach((type) => {
            const g = this.configuratorData[type];
            if (!g || !this.isGroupVisible(type)) return;

            const options = Array.isArray(g.options) ? g.options : [];
            const requiredOptions = options.filter(
                (opt) => opt?.required && this.isOptionVisibleByCondition(opt)
            );

            if (requiredOptions.length) {
                const selectedKey = this.selected[type]?.key || null;
                const ok = requiredOptions.some((opt) => opt.key === selectedKey);
                if (!ok) {
                    missing.push(type);
                }
                return;
            }

            if (this.isGroupRequired(type) && !this.selected[type]) {
                missing.push(type);
            }
        });
        return missing;
    }

    applyOptionConditions() {
        Object.entries(this.configuratorData).forEach(([k, g]) => {
            const sb = document.querySelector(
                `.js-configurator-sidebar[data-type="${k}"]`
            );
            if (!sb || !g) return;

            sb.querySelectorAll(`input[name="${k}"]`).forEach((input) => {
                const wrap = input.closest(".configurator-sidebar__option");
                if (!wrap) return;

                const val = input.value;
                const opt = this.findOption(k, val);

                const conditions = this.normalizeOptionConditions(opt?.condition);
                if (!conditions.length) {
                    wrap.style.display = "";
                    return;
                }

                const ok = conditions.some((cc) =>
                    this.evaluateOptionCondition(cc)
                );

                wrap.style.display = ok ? "" : "none";
                if (!ok && input.checked) {
                    input.checked = false;
                    this.clearSelection(k);
                }
            });
        });
    }

    isOptionVisibleByCondition(option) {
        const conditions = this.normalizeOptionConditions(option?.condition);
        if (!conditions.length) return true;
        return conditions.some((cc) => this.evaluateOptionCondition(cc));
    }

    findGroupKeyById(groupId) {
        for (const key of Object.keys(this.configuratorData)) {
            const g = this.configuratorData[key];
            if (g.id == groupId) return key;
            if (g.groups) {
                for (const subKey in g.groups) {
                    if (g.groups[subKey].id == groupId) return key;
                }
            }
        }
        return null;
    }

    getGroupOptions(type) {
        const g = this.configuratorData[type];
        if (!g) return [];
        if (Array.isArray(g.options)) return g.options;
        if (g.groups) {
            const collections = Array.isArray(g.groups)
                ? g.groups
                : Object.values(g.groups);
            return collections.flatMap((sub) => sub?.options || []);
        }
        return [];
    }

    groupHasVisibleOptions(type) {
        const options = this.getGroupOptions(type);
        if (!options.length) return false;
        return options.some((opt) => this.isOptionVisibleByCondition(opt));
    }

    normalizeShowIfConditions(raw) {
        if (!raw) return [];
        if (Array.isArray(raw)) {
            return raw
                .filter((c) => c && typeof c === "object")
                .map((c) =>
                    c.group ? c : c.group_key ? { ...c, group: c.group_key } : c
                )
                .filter((c) => c && (c.group || c.group_key));
        }
        if (typeof raw === "object") {
            const c = raw.group ? raw : raw.group_key ? { ...raw, group: raw.group_key } : raw;
            return c && (c.group || c.group_key) ? [c] : [];
        }
        return [];
    }

    evaluateShowIfCondition(c) {
        if (!c || !c.group) return true;
        let ctrl = null;
        if (c.group != null && String(c.group).match(/^\d+$/)) {
            ctrl = this.findGroupKeyById(c.group);
        } else if (c.group) {
            ctrl = c.group;
        }
        if (!ctrl) return true;
        const sel = this.selected[ctrl]?.key || null;
        if (c.operator === "equals" || c.operator === "=") return sel === c.key;
        if (c.operator === "not_equals" || c.operator === "!=") return sel !== c.key;
        return true;
    }

    normalizeOptionConditions(raw) {
        if (!raw) return [];
        if (Array.isArray(raw)) {
            return raw
                .filter((c) => c && typeof c === "object")
                .filter((c) => c.group_key);
        }
        if (typeof raw === "object") {
            return raw && raw.group_key ? [raw] : [];
        }
        return [];
    }

    evaluateOptionCondition(cc) {
        if (!cc || !cc.group_key) return true;
        const sel = this.selected[cc.group_key]?.key || null;
        if (cc.operator === "=") return sel === cc.value;
        if (cc.operator === "!=") return sel !== cc.value;
        return true;
    }

    bindPricePreviews() {
        document.body.addEventListener("change", (e) => {
            if (!e.target.matches("input[type='radio']")) return;

            this.applyConditions();
            this.applyOptionConditions();
            this.updateRequiredIndicators();

            const type = e.target.name;
            const key = e.target.value;
            const btn = document.querySelector(
                `.js-sidebar-confirm[data-type="${type}"]`
            );
            if (!btn) return;

            const opt = this.findOption(type, key);

            const price = opt ? parseFloat(opt.price).toFixed(2) : "0.00";
            btn.textContent =
                !key || price === "0.00"
                    ? "Zatwierdź (bez dopłaty)"
                    : `Zatwierdź (dopłata ${price} PLN)`;
        });
    }

    getOptionWrap(input) {
        return input.closest(
            ".configurator-sidebar__option, .configurator-sidebar__swatch-cell"
        );
    }

    updateRequiredIndicators() {
        const missing = new Set(this.getMissingRequiredGroups());
        Object.entries(this.configuratorData).forEach(([type, g]) => {
            const sb = document.querySelector(
                `.js-configurator-sidebar[data-type="${type}"]`
            );
            if (!sb || !g) return;

            sb.querySelectorAll(`input[name="${type}"]`).forEach((input) => {
                const wrap = this.getOptionWrap(input);
                if (!wrap) return;

                const opt = this.findOption(type, input.value);
                const isRequired = !!opt?.required;
                const isVisible = this.isOptionVisibleByCondition(opt);

                wrap.classList.toggle("-required", isRequired);
                wrap.classList.toggle(
                    "-required-missing",
                    isRequired && isVisible && missing.has(type)
                );
            });
        });
    }

    openSidebar(type) {
        document
            .querySelectorAll(".js-configurator-sidebar")
            .forEach((el) =>
                el.classList.remove("configurator-sidebar--active")
            );
        const sb = document.querySelector(
            `.js-configurator-sidebar[data-type="${type}"]`
        );
        sb?.classList.add("configurator-sidebar--active");
    }

    closeSidebar() {
        document
            .querySelectorAll(".js-configurator-sidebar")
            .forEach((el) =>
                el.classList.remove("configurator-sidebar--active")
            );
    }

    updateSummary() {
        const missing = new Set(this.getMissingRequiredGroups());
        Object.entries(this.selected).forEach(([type, data]) => {
            const item = document.querySelector(
                `.wc-product-configurator__item[data-configurator-open="${type}"]`
            );
            if (!item) return;
            const value = item.querySelector(".wc-product-configurator__value");
            const icon = item.querySelector(".wc-product-configurator__icon");

            item.classList.remove("-disabled");
            item.classList.add("-selected");
            item.classList.toggle("-required-missing", missing.has(type));
            value.textContent = data.label || "Wybierz";

            icon.innerHTML = "";
            if (data.image_url) {
                const img = document.createElement("img");
                img.className = "wc-product-configurator__image";
                img.src = data.image_url;
                icon.appendChild(img);
            } else {
                icon.innerHTML = `<img src="${this.placeholderUrl}" alt="" class="wc-product-configurator__image" />`;
            }
        });

        Object.keys(this.configuratorData).forEach((type) => {
            if (!this.selected[type]) {
                const item = document.querySelector(
                    `.wc-product-configurator__item[data-configurator-open="${type}"]`
                );
                if (!item) return;
                const value = item.querySelector(".wc-product-configurator__value");
                const icon = item.querySelector(".wc-product-configurator__icon");

                item.classList.remove("-selected");
                item.classList.add("-disabled");
                item.classList.toggle("-required-missing", missing.has(type));
                value.textContent = "Wybierz";
                icon.innerHTML = `<img src="${this.placeholderUrl}" alt="" class="wc-product-configurator__image" />`;
            }
        });

        this.updateAddToCartButton();
    }

    updateAddToCartButton() {
        const button = document.querySelector(".single_add_to_cart_button");
        if (!button) return;
        const missing = this.getMissingRequiredGroups();
        if (missing.length) {
            button.disabled = true;
            button.classList.add("disabled");
            button.textContent = "Wybierz wymagane opcje";
            return;
        }
        const base = parseFloat(button.dataset.basePrice || 0);
        const total = this.getTotalPrice(base).toFixed(2);
        const qty = document.querySelector("input.qty");
        const quantity = qty ? parseInt(qty.value, 10) || 1 : 1;
        if (!this.currentStockState.unlimited) {
            const maxQty = parseInt(this.currentStockState.qty, 10) || 0;
            if (quantity > maxQty) {
                button.disabled = true;
                button.classList.add("disabled");
                button.textContent = "Brak wystarczającej ilości";
                return;
            }
        }
        const full = (total * quantity).toFixed(2);
        button.disabled = false;
        button.classList.remove("disabled");
        button.textContent = `Dodaj do koszyka (${full} PLN)`;
    }

    bindQuantityChange() {
        const qty = document.querySelector("input.qty");
        if (!qty) return;
        qty.addEventListener("change", () => {
            this.enforceQtyAgainstStock();
            this.updateAddToCartButton();
        });
        qty.addEventListener("input", () => {
            this.enforceQtyAgainstStock();
            this.updateAddToCartButton();
        });
    }

    getTotalPrice(basePrice) {
        return (
            basePrice +
            Object.values(this.selected).reduce(
                (sum, o) => sum + parseFloat(o.price || 0),
                0
            )
        );
    }

    getSelectedOptionIds() {
        const out = {};
        Object.entries(this.selected).forEach(([k, d]) => {
            if (d.id != null) out[k] = d.id;
        });
        return out;
    }

    getCurrentProductId() {
        const stockProductId = this.stockValueEl?.dataset?.productId;
        if (stockProductId) {
            const parsed = parseInt(stockProductId, 10) || 0;
            if (parsed > 0) return parsed;
        }

        const button = document.querySelector(".js-add-to-cart-btn");
        if (button) {
            const parsed = parseInt(
                button.value || button.dataset.productId || 0,
                10
            ) || 0;
            if (parsed > 0) return parsed;
        }

        const formProductId =
            document.querySelector('form.cart input[name="product_id"]')?.value ||
            document.querySelector('form.cart input[name="add-to-cart"]')?.value ||
            "";
        return parseInt(formProductId, 10) || 0;
    }

    async refreshLiveStock() {
        if (!this.stockValueEl) return;
        const productId = this.getCurrentProductId();
        if (!productId) return;

        try {
            const params = new URLSearchParams({
                action: "mda_get_configurator_live_stock",
                product_id: String(productId),
                options: JSON.stringify(this.getSelectedOptionIds()),
                nonce: ajax.nonce,
            });

            const resp = await fetch("/wp-admin/admin-ajax.php", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: params.toString(),
            });
            const data = await resp.json();
            if (!data?.success || !data?.data) return;

            this.currentStockState = {
                unlimited: !!data.data.unlimited,
                qty:
                    data.data.qty === null || data.data.qty === undefined
                        ? null
                        : parseInt(data.data.qty, 10) || 0,
            };

            this.stockValueEl.textContent = data.data.text || "";
            this.enforceQtyAgainstStock();
            this.updateAddToCartButton();
        } catch (err) {
            console.error(err);
        }
    }

    enforceQtyAgainstStock() {
        const qty = document.querySelector("input.qty");
        if (!qty) return;

        if (this.currentStockState.unlimited) {
            qty.removeAttribute("max");
            this.syncQtyButtonsState(qty);
            return;
        }

        const maxQty = parseInt(this.currentStockState.qty, 10) || 0;
        if (maxQty <= 0) {
            qty.setAttribute("max", "1");
            if ((parseInt(qty.value, 10) || 1) !== 1) {
                qty.value = "1";
            }
            this.syncQtyButtonsState(qty);
            return;
        }

        qty.setAttribute("max", String(maxQty));

        const current = parseInt(qty.value, 10) || 1;
        if (current > maxQty) {
            qty.value = String(maxQty);
            qty.dispatchEvent(new Event("change", { bubbles: true }));
        }

        this.syncQtyButtonsState(qty);
    }

    syncQtyButtonsState(qtyInput) {
        const wrap = qtyInput.closest(".form-qty");
        if (!wrap) return;

        const decrease = wrap.querySelector(".form-qty__button.-decrease");
        const increase = wrap.querySelector(".form-qty__button.-increase");
        const min = parseInt(qtyInput.getAttribute("min"), 10) || 1;
        const rawMax = parseInt(qtyInput.getAttribute("max"), 10);
        const max = Number.isNaN(rawMax) ? Number.POSITIVE_INFINITY : rawMax;
        const value = parseInt(qtyInput.value, 10) || min;

        if (decrease) {
            decrease.classList.toggle("-disabled", value <= min);
        }
        if (increase) {
            increase.classList.toggle("-disabled", value >= max);
        }
    }

    bindAjaxAddToCart() {
        const button = document.querySelector(".js-add-to-cart-btn");

        if (!button) return;

        button.addEventListener("click", async (e) => {
            const missing = this.getMissingRequiredGroups();
            if (missing.length) {
                e.preventDefault();
                alert("Wybierz wymagane opcje przed dodaniem do koszyka.");
                return;
            }
            e.preventDefault();
            const productId = parseInt(button.value, 10);
            const qtyInput = document.querySelector("input.qty");
            const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
            const additionalOptions = Array.from(
                document.querySelectorAll('input[name="additional_options[]"]:checked')
            ).map((input) => input.value);

            try {
                const params = new URLSearchParams({
                    action: "add_configured_product_to_cart",
                    product_id: productId,
                    quantity,
                    options: JSON.stringify(this.getSelectedOptionIds()),
                    nonce: ajax.nonce,
                });

                additionalOptions.forEach((value) => {
                    params.append("additional_options[]", value);
                });

                const resp = await fetch("/wp-admin/admin-ajax.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: params,
                });
                const data = await resp.json();

                if (data?.data?.cartQty && this.headerCartQtyElement) {
                    this.headerCartQtyElement.textContent = data.data.cartQty;
                }

                if (data.success) {
                    const noticeWrapper =
                        document.querySelector(".woocommerce-notices-wrapper") ||
                        document.querySelector(".woocommerce");
                    if (noticeWrapper) {
                        const el = document.createElement("div");
                        el.className = "woocommerce-message";
                        el.innerHTML = "Produkt został dodany do koszyka.";
                        noticeWrapper.prepend(el);
                        document.body.dispatchEvent(new Event("added_to_cart"));
                    }
                    jQuery(document.body).trigger("wc_fragment_refresh");
                } else {
                    const noticeWrapper =
                        document.querySelector(".woocommerce-notices-wrapper") ||
                        document.querySelector(".woocommerce");
                    if (noticeWrapper) {
                        const el = document.createElement("div");
                        // Reuse the same DOM shape consumed by existing toast bridge.
                        // Event type decides error styling in handleWoocommerceToast.
                        el.className = "woocommerce-message";
                        el.innerHTML = data.data.message;
                        noticeWrapper.prepend(el);
                        document.body.dispatchEvent(new Event("added_to_cart_error"));
                    }
                }
            } catch (err) {
                alert("Błąd sieci.");
                console.error(err);
            }
        });
    }

    normalizeGalleryIndex(raw) {
        const parsed = parseInt(raw, 10);
        if (!Number.isFinite(parsed) || parsed < 1) return null;
        return parsed;
    }

    updateGallerySelection(option) {
        const galleryIndex = this.normalizeGalleryIndex(option?.gallery_index);
        if (!galleryIndex) return;
        document.dispatchEvent(
            new CustomEvent("mda_configurator_gallery_slide", {
                detail: { index: galleryIndex },
            })
        );
    }

    syncGalleryToSelected() {
        const selectedOptions = Object.values(this.selected || {});
        if (!selectedOptions.length) return;

        for (let i = selectedOptions.length - 1; i >= 0; i -= 1) {
            const galleryIndex = this.normalizeGalleryIndex(
                selectedOptions[i]?.gallery_index
            );
            if (!galleryIndex) continue;
            document.dispatchEvent(
                new CustomEvent("mda_configurator_gallery_slide", {
                    detail: { index: galleryIndex },
                })
            );
            return;
        }
    }
}
